/**
 * Qué cambió desde una foto anterior (V1.3, doc v5 §14/§15, RD-09): pinta el
 * `TaSnapshotDiff` de `taMetrics.snapshots.compare` para el filtro activo de la
 * vista Hiring Plan — búsquedas nuevas, cerradas, cambios de estado/OL y
 * movimientos de candidatos con nombre.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { cn } from '@/lib/utils';
import type { TaCandidatoMovimiento, TaSnapshotDiff } from '@/../../shared/ta-metrics';

const TONO: Record<TaCandidatoMovimiento['tipo'], string> = {
  entro: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  avanzo: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  retrocedio: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  salio: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  contratado: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
};

export function TaSnapshotDiffPanel({ diff, nota, loading, error }: { diff: TaSnapshotDiff | null | undefined; nota?: string; loading: boolean; error?: string | null }) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.hiring_plan.diff';
  if (loading) return <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground"><WinclapLoader size={16} /> {t(`${k}.loading`)}</div>;
  if (error) return <p className="flex items-center gap-1.5 py-2 text-sm text-destructive"><AlertTriangle className="h-4 w-4" /> {error}</p>;
  if (!diff) return <p className="py-2 text-sm text-muted-foreground">{nota ?? t(`${k}.sin_historico`)}</p>;
  const r = diff.resumen;
  const vacio = r.nuevas + r.cerradas + diff.cambios.length + diff.movimientos.length === 0;
  const stat = (label: string, n: number) => (
    <span className={cn('rounded-md border px-2 py-1 text-xs', n ? 'font-medium' : 'text-muted-foreground')}>{label}: <span className="tabular-nums">{n}</span></span>
  );
  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{t(`${k}.title`, { desde: diff.desde, hasta: diff.hasta })}</span>
        {stat(t(`${k}.nuevas`), r.nuevas)}
        {stat(t(`${k}.cerradas`), r.cerradas)}
        {stat(t(`${k}.cambios`), diff.cambios.length)}
        {stat(t(`${k}.entraron`), r.entraron)}
        {stat(t(`${k}.avanzaron`), r.avanzaron)}
        {stat(t(`${k}.retrocedieron`), r.retrocedieron)}
        {stat(t(`${k}.salieron`), r.salieron)}
        {stat(t(`${k}.contratados`), r.contratados)}
      </div>
      {vacio && <p className="text-sm text-muted-foreground">{t(`${k}.sin_cambios`)}</p>}
      {diff.nuevas.length > 0 && (
        <ul className="space-y-0.5 text-sm">
          <li className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.nuevas`)}</li>
          {diff.nuevas.map((x) => <li key={`n-${x.ID}-${x.posicion}`}><Badge variant="outline" className="mr-1.5 font-normal">{x.ID}</Badge>{x.posicion} · {x.estado} · {x.OL}</li>)}
        </ul>
      )}
      {diff.cerradas.length > 0 && (
        <ul className="space-y-0.5 text-sm">
          <li className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.cerradas`)}</li>
          {diff.cerradas.map((x) => <li key={`c-${x.ID}-${x.posicion}`}><Badge variant="outline" className="mr-1.5 font-normal">{x.ID}</Badge>{x.posicion} · {x.estado_anterior} → {x.estado_actual}{x.contratado ? ` · ${x.contratado}` : ''}</li>)}
        </ul>
      )}
      {diff.cambios.length > 0 && (
        <ul className="space-y-0.5 text-sm">
          <li className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.cambios`)}</li>
          {diff.cambios.map((x) => <li key={`e-${x.ID}-${x.posicion}`}><Badge variant="outline" className="mr-1.5 font-normal">{x.ID}</Badge>{x.posicion} · {x.de} → {x.a}</li>)}
        </ul>
      )}
      {diff.movimientos.length > 0 && (
        <ul className="space-y-0.5 text-sm">
          <li className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`${k}.movimientos`)}</li>
          {diff.movimientos.map((m, i) => (
            <li key={`m-${m.ID}-${m.nombre}-${i}`} className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className={cn('border-transparent font-medium', TONO[m.tipo])}>{t(`${k}.tipo_${m.tipo}`)}</Badge>
              <span className="font-medium">{m.nombre}</span>
              <span className="text-muted-foreground">· {m.posicion} ({m.ID}) · {m.de ?? '—'} → {m.a ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
      {nota && <p className="text-xs text-muted-foreground">{nota}</p>}
    </div>
  );
}
